import type { ImprovementOrigin } from '@zeus/index'
import type { ActionForm } from './action-form'
import type { RiskForm } from './risk-form'
import { useMutation } from '@apollo/client/react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { GQL_CREATE_IMPROVEMENT_ACTION, GQL_CREATE_QUALITY_RISK, GQL_UPDATE_IMPROVEMENT_ACTION, GQL_UPDATE_QUALITY_RISK } from '~/gql/quality/quality'

/** Ce que le formulaire d'une action envoie : vide = effacé. */
function actionInput(form: ActionForm) {
  return {
    origin: form.origin as ImprovementOrigin,
    title: form.title,
    description: form.description,
    owner: form.owner || null,
    dueDate: form.dueDate || null,
  }
}

function riskInput(form: RiskForm) {
  return {
    title: form.title,
    description: form.description,
    likelihood: form.likelihood,
    impact: form.impact,
    mitigation: form.mitigation || null,
    owner: form.owner || null,
  }
}

/** Écritures des registres qualité (#328), avec leurs messages de retour. */
export function useQualityMutations(onDone: () => void) {
  const { t } = useTranslation()
  const [createAction, created] = useMutation(GQL_CREATE_IMPROVEMENT_ACTION)
  const [updateAction, updated] = useMutation(GQL_UPDATE_IMPROVEMENT_ACTION)
  const [createRisk, riskCreated] = useMutation(GQL_CREATE_QUALITY_RISK)
  const [updateRisk, riskUpdated] = useMutation(GQL_UPDATE_QUALITY_RISK)

  const run = async (write: () => Promise<unknown>) => {
    try {
      await write()
      toast.success(t('quality.saved'))
      onDone()
    } catch {
      toast.error(t('quality.saveFailed'))
    }
  }

  return {
    busy: created.loading || updated.loading || riskCreated.loading || riskUpdated.loading,
    createAction: (form: ActionForm) => run(() => createAction({ variables: { input: actionInput(form) } })),
    updateAction: (id: string, form: ActionForm) =>
      run(() => updateAction({ variables: { input: { id, ...actionInput(form), status: form.status, effect: form.effect || null } } })),
    createRisk: (form: RiskForm) => run(() => createRisk({ variables: { input: riskInput(form) } })),
    updateRisk: (id: string, form: RiskForm) => run(() => updateRisk({ variables: { input: { id, ...riskInput(form), status: form.status } } })),
  }
}
